import { LogHttpError } from "@/models/decorators/http.decorators";
import axios, { AxiosResponse } from "axios";
import { AddressInputContainer } from "../entity-settings";

const API_URL = process.env.VUE_APP_API_URL;

/**
 * Единый axios-инстанс для всех запросов проекта.
 * JWT подставляется автоматически request-интерцептором (источник — localStorage.currentSession),
 * протухшая сессия (401) централизованно обрабатывается response-интерцептором.
 */
export const apiClient = axios.create({ baseURL: API_URL });

// автоматическая подстановка токена авторизации
apiClient.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem("currentSession");
    if (raw) {
      const session = JSON.parse(raw);
      if (session && session.jwtToken) {
        config.headers.set("Authorization", "Bearer " + session.jwtToken);
      }
    }
  } catch (e) {
    /* битый currentSession — игнорируем, запрос уйдёт без токена */
  }
  return config;
});

// централизованная обработка протухшей сессии
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error && error.response && error.response.status === 401) {
      // лениво — иначе циклические импорты (API-классы импортируются сторами)
      const [{ clearSession }, routerModule] = await Promise.all([
        import("@/composables/useSession"),
        import("@/router"),
      ]);
      const router = routerModule.default;
      if (router.currentRoute.value.path !== "/login") {
        clearSession();
        router.push({
          path: "/login",
          query: { redirect: router.currentRoute.value.fullPath },
        });
      }
    }
    return Promise.reject(error);
  }
);

export interface CommonHttpRequest {
  jwt: string;
  base_url: string;
  use_token: boolean;
}
export interface CommonRequest {
  ChangerId: number;
  SessionKey: string;
}

export interface SingleIdRequest extends CommonRequest {
  Id: number;
}

export interface PersonEditRequest {
    PersonId: number,
    Name: string;
    Post: string;
    Contacts: string;
    InputContainer: AddressInputContainer;
    Tags: string[];
}

/**
 * Тонкая обёртка над общим axios-инстансом (apiClient).
 * Токен авторизации ставит request-интерцептор apiClient — вручную не передаётся.
 * Конструктор совместим с сигнатурами обеих веток:
 *   - stage: new CommonHttp()           / new CommonHttp(true)              // true => old_api
 *   - pre:   new CommonHttp(url, jwt)    / new CommonHttp(url, jwt, old, form)
 * jwt из вызовов ветки pre игнорируется (токен ставит интерцептор). Базовый URL по умолчанию
 * задаёт apiClient; если в pre-вызове передан URL, отличный от дефолтного (напр. 1С в корне
 * хоста, без /api), он применяется как baseURL запроса. old_api => Content-Type json,
 * form_api => multipart/form-data.
 */
export class CommonHttp implements CommonHttpRequest {
  // поля интерфейса CommonHttpRequest сохранены для обратной совместимости (методы их не используют)
  jwt = "";
  base_url = "";
  use_token = true;

  private old: boolean;
  private form: boolean;
  private baseUrlOverride?: string;

  constructor(oldApiOrBaseUrl: boolean | string = false, _baseJwt?: string, old_api = false, form_api = false) {
    if (typeof oldApiOrBaseUrl === "string") {
      // вызов в стиле pre: (baseUrl, jwt, old_api, form_api)
      this.old = old_api;
      this.form = form_api;
      // базовый URL применяем только если он отличается от дефолтного (напр. 1С в корне хоста, без /api)
      if (oldApiOrBaseUrl && oldApiOrBaseUrl !== API_URL) {
        this.baseUrlOverride = oldApiOrBaseUrl;
      }
    } else {
      // вызов в стиле stage: (old_api)
      this.old = oldApiOrBaseUrl;
      this.form = false;
    }
  }

  private getHeaders() {
    return {
      ...(this.old ? { "Content-Type": "application/json" } : {}),
      ...(this.form ? { "Content-Type": "multipart/form-data" } : {}),
    };
  }

  // заголовки + опциональный override базового URL; явный opts.baseURL имеет приоритет
  private getConfig(opts: any = {}) {
    return {
      headers: this.getHeaders(),
      ...(this.baseUrlOverride ? { baseURL: this.baseUrlOverride } : {}),
      ...opts,
    };
  }

  @LogHttpError([404, 400, 401, 490, 500])
  get(url: string, opts: any = {}): Promise<AxiosResponse<any, any>> {
    return apiClient.get(url, this.getConfig(opts))
  }

  @LogHttpError([404, 400, 401, 490, 500])
  post(url: string, data: any, opts: any = {}): Promise<AxiosResponse<any, any>> {
    return apiClient.post(url, data, this.getConfig(opts))
  }

  @LogHttpError([404, 400, 401, 490, 500])
  put(url: string, data: any, opts: any = {}): Promise<AxiosResponse<any, any>> {
    return apiClient.put(url, data, this.getConfig(opts))
  }

  @LogHttpError([404, 400, 401, 490, 500])
  delete(url: string, data?: any): Promise<AxiosResponse<any, any>> {
    return apiClient.delete(url, this.getConfig({ data }))
  }

  @LogHttpError([404, 400, 401, 490, 500])
  patch(url: string, data: any, opts: any = {}): Promise<AxiosResponse<any, any>> {
    return apiClient.patch(url, data, this.getConfig(opts))
  }
}
